/**
 * Wordle Board Component
 *
 * Renders the 6x5 grid of letter tiles, on-screen keyboard,
 * statistics modal, and game completion UI.
 *
 * Features:
 * - Flip animation on reveal
 * - Shake animation for invalid words
 * - Bounce animation for winning row
 * - Color-coded keyboard
 * - Physical keyboard support
 * - Share button with emoji grid
 * - Statistics display with guess distribution
 * - Countdown timer to next daily word
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  WordleGameState,
  WordleStats,
  LetterState,
  WORD_LENGTH,
  MAX_GUESSES,
  KEYBOARD_ROWS,
} from '../types';
import './WordleBoard.css';

interface WordleBoardProps {
  gameState: WordleGameState;
  onKeyPress: (key: string) => void;
  onPlayAgain: () => void;
  onBackToMenu: () => void;
  onExit: () => void;
  toastMessage: string | null;
  shakeRow: boolean;
  timer: number;
  stats: WordleStats;
  shareText: string;
  timeUntilNext: number;
}

export const WordleBoard: React.FC<WordleBoardProps> = ({
  gameState,
  onKeyPress,
  onPlayAgain,
  onBackToMenu,
  onExit,
  toastMessage,
  shakeRow,
  timer,
  stats,
  shareText,
  timeUntilNext,
}) => {
  const [showStats, setShowStats] = useState(false);
  const [revealedRows, setRevealedRows] = useState<Set<number>>(new Set());
  const [countdown, setCountdown] = useState(timeUntilNext);
  const boardRef = useRef<HTMLDivElement>(null);

  // Physical keyboard support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        onKeyPress('ENTER');
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        onKeyPress('BACKSPACE');
      } else if (/^[a-zA-Z]$/.test(e.key)) {
        e.preventDefault();
        onKeyPress(e.key.toUpperCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKeyPress]);

  // Show stats modal when game ends
  useEffect(() => {
    if (gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') {
      const timeout = setTimeout(() => setShowStats(true), 1500);
      return () => clearTimeout(timeout);
    }
  }, [gameState.gameStatus]);

  // Track revealed rows for animation
  useEffect(() => {
    if (gameState.guesses.length > 0) {
      const lastRow = gameState.guesses.length - 1;
      if (!revealedRows.has(lastRow)) {
        // Delay reveal for flip animation
        const timeout = setTimeout(() => {
          setRevealedRows(prev => new Set([...prev, lastRow]));
        }, 300);
        return () => clearTimeout(timeout);
      }
    }
  }, [gameState.guesses.length, revealedRows]);

  // Countdown timer
  useEffect(() => {
    if (gameState.gameStatus !== 'playing') {
      const interval = setInterval(() => {
        setCountdown(prev => Math.max(0, prev - 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameState.gameStatus]);

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatCountdown = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleShare = useCallback(async () => {
    try {
      if (navigator.share) {
        await navigator.share({ text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        // Show a brief toast
      }
    } catch {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
      } catch {
        // Silent fail
      }
    }
  }, [shareText]);

  const getLetterStateClass = (state: LetterState): string => {
    switch (state) {
      case 'correct': return 'wordle-tile--correct';
      case 'present': return 'wordle-tile--present';
      case 'absent': return 'wordle-tile--absent';
      case 'tbd': return 'wordle-tile--tbd';
      default: return '';
    }
  };

  const getKeyStateClass = (state?: LetterState): string => {
    switch (state) {
      case 'correct': return 'wordle-key--correct';
      case 'present': return 'wordle-key--present';
      case 'absent': return 'wordle-key--absent';
      default: return '';
    }
  };

  // Render the game board grid
  const renderGrid = () => {
    const rows = [];

    for (let rowIdx = 0; rowIdx < MAX_GUESSES; rowIdx++) {
      const isCurrentRow = rowIdx === gameState.currentRow;
      const isSubmitted = rowIdx < gameState.guesses.length;
      const isWinRow = gameState.gameStatus === 'won' && rowIdx === gameState.guesses.length - 1;
      const shouldShake = shakeRow && isCurrentRow;

      const tiles = [];

      for (let colIdx = 0; colIdx < WORD_LENGTH; colIdx++) {
        let letter = '';
        let stateClass = '';
        let hasLetter = false;
        const flipDelay = colIdx * 300;

        if (isSubmitted) {
          const guess = gameState.guesses[rowIdx];
          letter = guess.letters[colIdx].letter;
          stateClass = getLetterStateClass(guess.letters[colIdx].state);
          hasLetter = true;
        } else if (isCurrentRow && colIdx < gameState.currentGuess.length) {
          letter = gameState.currentGuess[colIdx];
          hasLetter = true;
          stateClass = 'wordle-tile--tbd';
        }

        tiles.push(
          <div
            key={`${rowIdx}-${colIdx}`}
            className={`wordle-tile ${stateClass} ${hasLetter ? 'wordle-tile--filled' : ''} ${isSubmitted ? 'wordle-tile--revealed' : ''}`}
            style={isSubmitted ? { animationDelay: `${flipDelay}ms` } : undefined}
            data-state={isSubmitted ? gameState.guesses[rowIdx].letters[colIdx].state : (hasLetter ? 'tbd' : 'empty')}
          >
            <span className="wordle-tile__letter">{letter}</span>
          </div>
        );
      }

      rows.push(
        <div
          key={rowIdx}
          className={`wordle-row ${shouldShake ? 'wordle-row--shake' : ''} ${isWinRow ? 'wordle-row--bounce' : ''}`}
        >
          {tiles}
        </div>
      );
    }

    return rows;
  };

  // Render the on-screen keyboard
  const renderKeyboard = () => {
    return (
      <div className="wordle-keyboard">
        {KEYBOARD_ROWS.map((row, rowIdx) => (
          <div key={rowIdx} className="wordle-keyboard__row">
            {row.map(key => {
              const isSpecial = key === 'ENTER' || key === 'BACKSPACE';
              const keyState = !isSpecial ? gameState.keyboardState[key] : undefined;
              const stateClass = getKeyStateClass(keyState);
              const displayKey = key === 'BACKSPACE' ? '⌫' : key;

              return (
                <button
                  key={key}
                  className={`wordle-key ${stateClass} ${isSpecial ? 'wordle-key--wide' : ''}`}
                  onClick={() => onKeyPress(key)}
                  aria-label={key}
                  data-key={key}
                >
                  {displayKey}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Render stats modal
  const renderStatsModal = () => {
    if (!showStats) return null;

    const maxDistribution = Math.max(...stats.guessDistribution, 1);

    return (
      <div className="wordle-stats-overlay" onClick={() => setShowStats(false)}>
        <div className="wordle-stats-modal" onClick={e => e.stopPropagation()}>
          <button
            className="wordle-stats-modal__close"
            onClick={() => setShowStats(false)}
            aria-label="Close"
          >
            &times;
          </button>

          <h2 className="wordle-stats-modal__title">Statistics</h2>

          <div className="wordle-stats-modal__summary">
            <div className="wordle-stat">
              <span className="wordle-stat__value">{stats.gamesPlayed}</span>
              <span className="wordle-stat__label">Played</span>
            </div>
            <div className="wordle-stat">
              <span className="wordle-stat__value">{stats.winPercentage}</span>
              <span className="wordle-stat__label">Win %</span>
            </div>
            <div className="wordle-stat">
              <span className="wordle-stat__value">{stats.currentStreak}</span>
              <span className="wordle-stat__label">Current Streak</span>
            </div>
            <div className="wordle-stat">
              <span className="wordle-stat__value">{stats.maxStreak}</span>
              <span className="wordle-stat__label">Max Streak</span>
            </div>
          </div>

          <h3 className="wordle-stats-modal__subtitle">Guess Distribution</h3>
          <div className="wordle-distribution">
            {stats.guessDistribution.map((count, idx) => (
              <div key={idx} className="wordle-distribution__row">
                <span className="wordle-distribution__label">{idx + 1}</span>
                <div className="wordle-distribution__bar-container">
                  <div
                    className={`wordle-distribution__bar ${
                      gameState.gameStatus === 'won' && gameState.guesses.length === idx + 1
                        ? 'wordle-distribution__bar--highlight'
                        : ''
                    }`}
                    style={{ width: `${Math.max((count / maxDistribution) * 100, 7)}%` }}
                  >
                    <span className="wordle-distribution__count">{count}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {gameState.gameStatus !== 'playing' && (
            <div className="wordle-stats-modal__footer">
              {gameState.mode === 'daily' && (
                <div className="wordle-stats-modal__countdown">
                  <span className="wordle-stats-modal__countdown-label">Next Word</span>
                  <span className="wordle-stats-modal__countdown-time">
                    {formatCountdown(countdown)}
                  </span>
                </div>
              )}
              <div className="wordle-stats-modal__actions">
                <button className="wordle-stats-modal__share-btn" onClick={handleShare}>
                  Share 📤
                </button>
                {gameState.mode === 'practice' && (
                  <button className="wordle-stats-modal__play-btn" onClick={onPlayAgain}>
                    Play Again
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="wordle-board" ref={boardRef}>
      {/* Toast notification */}
      {toastMessage && (
        <div className="wordle-toast">
          <span className="wordle-toast__message">{toastMessage}</span>
        </div>
      )}

      {/* Game info bar */}
      <div className="wordle-board__info">
        <span className="wordle-board__mode">
          {gameState.mode === 'daily' ? `Daily #${gameState.puzzleNumber}` : 
           gameState.mode === 'practice' ? 'Practice' : 'Competitive'}
        </span>
        {gameState.hardMode && <span className="wordle-board__hard-badge">HARD</span>}
        <span className="wordle-board__timer">{formatTime(timer)}</span>
      </div>

      {/* Game grid */}
      <div className="wordle-grid">
        {renderGrid()}
      </div>

      {/* Result message */}
      {gameState.gameStatus === 'lost' && (
        <div className="wordle-board__answer">
          The word was: <strong>{gameState.targetWord}</strong>
        </div>
      )}

      {/* On-screen keyboard */}
      {renderKeyboard()}

      {/* Action buttons when game is over */}
      {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && (
        <div className="wordle-board__actions">
          <button className="wordle-board__stats-btn" onClick={() => setShowStats(true)}>
            📊 Statistics
          </button>
          <button className="wordle-board__share-btn" onClick={handleShare}>
            📤 Share
          </button>
          {gameState.mode === 'practice' && (
            <button className="wordle-board__play-again-btn" onClick={onPlayAgain}>
              🔄 Play Again
            </button>
          )}
        </div>
      )}

      {/* Stats modal */}
      {renderStatsModal()}
    </div>
  );
};
